import React, { useMemo, useState } from "react";
import "./imageHistory.scss";
import {
  HiOutlineClock,
  HiChevronDown,
  HiSquares2X2,
  HiBars3,
  HiFunnel,
  HiEye,
  HiArrowDownTray,
  HiLink,
  HiFolder,
  HiCheck,
  HiTrash,
  HiPencilSquare,
  HiStar,
  HiOutlineStar,
} from "react-icons/hi2";
import { auth, db, storage } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  ref as sRef,
  uploadBytes,
  getDownloadURL,
  getMetadata,
} from "firebase/storage";
function FolderSelect({ folders = [], value, onChange, onCreateFolder }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const opts = React.useMemo(
    () => folders.filter((f) => f !== "All"),
    [folders]
  );

  const [newMode, setNewMode] = React.useState(false);
  const [newName, setNewName] = React.useState("");

  React.useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const current = value || "Unsorted";

  return (
    <div className={`ihx-selectFolder ${open ? "is-open" : ""}`} ref={wrapRef}>
      <button
        type="button"
        className="ihx-selectButton"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <HiFolder className="ihx-selectIcon" />
        <span className="ihx-selectLabel" title={current}>
          {current}
        </span>
        <HiChevronDown className="ihx-selectCaret" />
      </button>
      {open && (
        <ul className="ihx-actionMenu" role="listbox">
          {opts.map((name) => (
            <li key={name} role="option" aria-selected={name === current}>
              <button
                type="button"
                className={`ihx-option ${name === current ? "is-active" : ""}`}
                onClick={() => {
                  onChange && onChange(name);
                  setOpen(false);
                }}
              >
                <span className="ihx-optionIcon">
                  <HiFolder />
                </span>
                <span className="ihx-optionLabel" title={name}>
                  {name}
                </span>
                {name === current && (
                  <span className="ihx-optionCheck">
                    <HiCheck />
                  </span>
                )}
              </button>
            </li>
          ))}
          <li>
            {!newMode ? (
              <button
                type="button"
                className="ihx-option"
                onClick={() => setNewMode(true)}
              >
                <span className="ihx-optionIcon">
                  <HiFolder />
                </span>
                <span className="ihx-optionLabel">New Folder</span>
              </button>
            ) : (
              <div
                className="ihx-inlineNew"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  autoFocus
                  className="ihx-inlineNew__input"
                  placeholder="Folder name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const name = (newName || "").trim();
                      if (!name) return;
                      onCreateFolder && onCreateFolder(name);
                      onChange && onChange(name);
                      setNewName("");
                      setNewMode(false);
                      setOpen(false);
                    }
                    if (e.key === "Escape") {
                      setNewMode(false);
                      setNewName("");
                    }
                  }}
                />
                <button
                  className="ihx-inlineNew__btn ihx-inlineNew__btn--primary"
                  onClick={() => {
                    const name = (newName || "").trim();
                    if (!name) return;
                    onCreateFolder && onCreateFolder(name);
                    onChange && onChange(name);
                    setNewName("");
                    setNewMode(false);
                    setOpen(false);
                  }}
                >
                  Create
                </button>
                <button
                  className="ihx-inlineNew__btn"
                  onClick={() => {
                    setNewMode(false);
                    setNewName("");
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </li>
        </ul>
      )}
    </div>
  );
}

export default function ImageHistory({
  userPictures = [],
  selectedImage,
  setSelectedImage,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [view, setView] = useState("grid"); // grid | list (future)
  const [sort, setSort] = useState("newest");
  // near your other state
  const [sortOpen, setSortOpen] = React.useState(false);
  const sortWrapRef = React.useRef(null);
  const [uid, setUid] = useState(null);
  const [folders, setFolders] = useState(["All", "Unsorted"]);
  const [selectedFolder, setSelectedFolder] = useState("All");
  const [folderMap, setFolderMap] = useState({}); // url -> folder name
  const [newFolderName, setNewFolderName] = useState("");
  const [showAddTop, setShowAddTop] = useState(false);
  const [showAddSide, setShowAddSide] = useState(false);
  const [menuOpenFor, setMenuOpenFor] = useState(null); // url currently showing the action menu
  const [favorites, setFavorites] = useState(new Set());
  // Cache natural dimensions per image URL so we can classify by aspect ratio
  const [imgDims, setImgDims] = useState({}); // { [url]: { w, h } }
  const [moreOpen, setMoreOpen] = useState(false);
  const moreWrapRef = React.useRef(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageRows, setManageRows] = useState([]); // [{name, edit}]
  // Bulk actions state
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const bulkWrapRef = React.useRef(null);
  // Timeline filter state
  const [timelineOpen, setTimelineOpen] = React.useState(false);
  const timelineWrapRef = React.useRef(null);
  const [dateRange, setDateRange] = React.useState({ start: null, end: null }); // JS Date or null
  const [customStart, setCustomStart] = React.useState(""); // yyyy-mm-dd
  const [customEnd, setCustomEnd] = React.useState("");
  // Map of created timestamps per URL (ms since epoch)
  const [createdMap, setCreatedMap] = useState({});
  React.useEffect(() => {
    const onDoc = (e) => {
      if (!timelineWrapRef.current) return;
      if (!timelineWrapRef.current.contains(e.target)) setTimelineOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setTimelineOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);
  React.useEffect(() => {
    const onDoc = (e) => {
      if (!bulkWrapRef.current) return;
      if (!bulkWrapRef.current.contains(e.target)) setBulkOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setBulkOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);
  // Selection state for bulk actions
  const [selectedSet, setSelectedSet] = useState(new Set());
  const isSelected = (url) => selectedSet.has(url);
  const clearSelection = () => setSelectedSet(new Set());
  // Upload overlay state
  const fileInputRef = React.useRef(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadItems, setUploadItems] = useState([]); // [{file, name, folder, preview}]
  const [uploadFolder, setUploadFolder] = useState("Unsorted");
  // Local buffer of URLs uploaded in this session so the grid refreshes instantly
  const [justUploaded, setJustUploaded] = useState([]); // string[] URLs
  // Upload handlers
  const openFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };
  const onFileChosen = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const items = files.map((f) => ({
      file: f,
      name: f.name.replace(/\.[^.]+$/, ""),
      folder: selectedFolder === "All" ? "Unsorted" : selectedFolder,
      preview: URL.createObjectURL(f),
    }));
    setUploadItems(items);
    setUploadFolder(selectedFolder === "All" ? "Unsorted" : selectedFolder);
    setUploadOpen(true);
    // reset input so selecting the same files again re-triggers change
    e.target.value = "";
  };
  const closeUploadOverlay = () => {
    // cleanup previews
    uploadItems.forEach((it) => it.preview && URL.revokeObjectURL(it.preview));
    setUploadItems([]);
    setUploadOpen(false);
  };
  const confirmUpload = async () => {
    if (!uid || !uploadItems.length) return;

    const targetFolder =
      uploadFolder || (selectedFolder === "All" ? "Unsorted" : selectedFolder);

    // Apply the selected folder to all items
    const itemsWithFolder = uploadItems.map((it) => ({
      ...it,
      folder: targetFolder,
    }));

    try {
      // sequential to be gentle; can be Promise.all if you prefer
      for (const it of itemsWithFolder) {
        const file = it.file;
        const name = safeName(it.name || file.name, file.type);
        const path = `pictures/${uid}/${name}`;
        const r = sRef(storage, path);

        await uploadBytes(r, file, { contentType: file.type });

        const url = await getDownloadURL(r);
        const meta = await getMetadata(r);
        // Prefer Storage's last modified; fall back to timeCreated
        const lastModifiedMs = meta.updated
          ? Date.parse(meta.updated)
          : meta.timeCreated
            ? Date.parse(meta.timeCreated)
            : Date.now();

        // persist folder + created timestamp alongside the URL
        const id = idForUrl(url);
        await setDoc(
          doc(db, "imageFolders", uid, "items", id),
          { url, folder: it.folder, favorite: false, created: lastModifiedMs },
          { merge: true }
        );

        // optimistic local caches so timeline/folders update immediately
        setFolderMap((m) => ({ ...m, [url]: it.folder }));
        setCreatedMap((cm) => ({ ...cm, [url]: lastModifiedMs }));
        setJustUploaded((prev) => (prev.includes(url) ? prev : [...prev, url]));
      }
    } catch (e) {
      console.warn("[ImageHistory] upload failed", e);
    } finally {
      // cleanup previews + close overlay
      closeUploadOverlay();
    }
  };
  React.useEffect(() => {
    let abort = false;

    const hydrate = async () => {
      if (!uid) return;
      const all = userPictures || [];
      // only fetch a few at a time to avoid hammering
      const missing = all.filter((u) => !createdMap[u]).slice(0, 25);
      if (!missing.length) return;

      try {
        const pairs = await Promise.all(
          missing.map(async (u) => {
            try {
              const rr = sRef(storage, u);
              const meta = await getMetadata(rr);
              const lastMs = meta.updated
                ? Date.parse(meta.updated)
                : meta.timeCreated
                  ? Date.parse(meta.timeCreated)
                  : null;
              return [u, lastMs];
            } catch {
              return [u, null];
            }
          })
        );
        if (abort) return;
        setCreatedMap((prev) => {
          const next = { ...prev };
          pairs.forEach(([u, lastMs]) => {
            if (lastMs) next[u] = lastMs;
          });
          return next;
        });
      } catch {
        // ignore; best-effort hydration
      }
    };

    hydrate();
    return () => {
      abort = true;
    };
  }, [uid, userPictures, createdMap]);

  // Reset local buffer after parent updates: clear justUploaded if parent userPictures contains those URLs
  React.useEffect(() => {
    if (!justUploaded.length) return;
    const all = new Set(userPictures || []);
    const remaining = justUploaded.filter((u) => !all.has(u));
    if (remaining.length !== justUploaded.length) setJustUploaded(remaining);
  }, [userPictures, justUploaded]);
  React.useEffect(() => {
    const onDoc = (e) => {
      if (!sortWrapRef.current) return;
      if (!sortWrapRef.current.contains(e.target)) setSortOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setSortOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);
  React.useEffect(() => {
    const onDoc = (e) => {
      if (!moreWrapRef.current) return;
      if (!moreWrapRef.current.contains(e.target)) setMoreOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // load folders + item mappings when uid changes
  // (above comment moved down)

  // --- Folder management helpers ---
  const openManage = () => {
    setManageRows(
      (folders || [])
        .filter((f) => f && !["All", "Unsorted"].includes(f))
        .map((name) => ({ name, edit: name }))
    );
    setManageOpen(true);
  };

  const renameFolder = async (oldName, newName) => {
    const clean = (newName || "").trim();
    if (!uid || !clean || ["All", "Unsorted"].includes(oldName)) return;
    const snap = await getDocs(collection(db, "imageFolders", uid, "folders"));
    const match = snap.docs.find((d) => (d.data().name || "") === oldName);
    if (match) {
      await updateDoc(doc(db, "imageFolders", uid, "folders", match.id), {
        name: clean,
      });
    }
    setFolders((prev) => prev.map((n) => (n === oldName ? clean : n)));
    setFolderMap((m) => {
      const copy = { ...m };
      Object.keys(copy).forEach((url) => {
        if (copy[url] === oldName) copy[url] = clean;
      });
      return copy;
    });
    if (selectedFolder === oldName) setSelectedFolder(clean);
  };

  const deleteFolderByName = async (name) => {
    if (!uid || !name || ["All", "Unsorted"].includes(name)) return;
    const fSnap = await getDocs(collection(db, "imageFolders", uid, "folders"));
    const match = fSnap.docs.find((d) => (d.data().name || "") === name);
    if (match) {
      await deleteDoc(doc(db, "imageFolders", uid, "folders", match.id));
    }
    const iSnap = await getDocs(collection(db, "imageFolders", uid, "items"));
    for (const d of iSnap.docs) {
      const data = d.data();
      if (data.folder === name) {
        await setDoc(
          doc(db, "imageFolders", uid, "items", d.id),
          { folder: "Unsorted" },
          { merge: true }
        );
      }
    }
    setFolders((prev) => prev.filter((n) => n !== name));
    setFolderMap((m) => {
      const copy = { ...m };
      Object.keys(copy).forEach((url) => {
        if (copy[url] === name) copy[url] = "Unsorted";
      });
      return copy;
    });
    if (selectedFolder === name) setSelectedFolder("Unsorted");
  };

  // --- End folder management helpers ---
  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUid(user.uid);
      } else {
        setUid(null);
      }
    });
    return () => unsub();
  }, []);

  React.useEffect(() => {
    const onDoc = (e) => {
      const withinMenu =
        e.target.closest &&
        e.target.closest(".ihx-actionMenu, .cu-cell--action");
      if (!withinMenu) setMenuOpenFor(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpenFor(null);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // load folders + item mappings when uid changes
  React.useEffect(() => {
    const load = async () => {
      if (!uid) return;
      // folders
      const fSnap = await getDocs(
        collection(db, "imageFolders", uid, "folders")
      );
      const names = [
        "All",
        "Unsorted",
        ...fSnap.docs.map((d) => d.data().name).filter(Boolean),
      ];
      // items mapping (url -> folder)
      // hydrate favorites and created timestamps
      const iSnap = await getDocs(collection(db, "imageFolders", uid, "items"));
      const map = {};
      const favs = new Set();
      const cMap = {};
      iSnap.forEach((d) => {
        const { url, folder, favorite, createdAt, created, ts } = d.data();
        if (url && folder) map[url] = folder;
        if (url && favorite) favs.add(url);
        // support several possible timestamp field names: createdAt (Firestore Timestamp), created (ms), ts (ms)
        let ms = null;
        if (createdAt && typeof createdAt.seconds === "number") {
          ms =
            createdAt.seconds * 1000 +
            (createdAt.nanoseconds
              ? Math.floor(createdAt.nanoseconds / 1e6)
              : 0);
        } else if (typeof created === "number") {
          ms = created;
        } else if (typeof ts === "number") {
          ms = ts;
        }
        if (url && ms) cMap[url] = ms;
      });
      setFolders(Array.from(new Set(names)));
      setFolderMap(map);
      setFavorites(favs);
      setCreatedMap(cMap);
      console.log("Found favorites:", favs);
    };
    load();
  }, [uid]);

  const filtered = useMemo(() => {
    // Merge incoming pictures with anything just uploaded locally so UI refreshes instantly
    const allPics = Array.from(
      new Set([...(userPictures || []), ...justUploaded])
    );

    const base = allPics.map((url, idx) => {
      const m = String(url).match(/pictures%2F[^%]+%2F([^?]+)\?/);
      const filename = m ? decodeURIComponent(m[1]) : `Image ${idx}`;
      const folder = folderMap[url] || "Unsorted";
      const created = createdMap[url] ? new Date(createdMap[url]) : null;
      return { url, filename, idx, folder, created };
    });

    const byFolder = base.filter((it) =>
      selectedFolder === "All" ? true : it.folder === selectedFolder
    );
    const byDate = byFolder.filter((it) => {
      if (!dateRange || (!dateRange.start && !dateRange.end)) return true;
      if (!it.created) return false; // no timestamp, hide when a range is applied
      const t = it.created.getTime();
      if (dateRange.start && t < dateRange.start.getTime()) return false;
      if (dateRange.end) {
        // include end-date through the end of the day
        const endMs = dateRange.end.getTime() + 24 * 60 * 60 * 1000 - 1;
        if (t > endMs) return false;
      }
      return true;
    });
    const byTab = byDate.filter((it) => {
      if (activeTab === "all") return true;
      if (activeTab === "favorites") return favorites.has(it.url);

      const dims = imgDims[it.url];
      if (dims && dims.w && dims.h) {
        const { w, h } = dims;
        if (activeTab === "portrait") return h > w; // true portrait
        if (activeTab === "landscape") return w > h; // true landscape
        return true;
      }

      // Fallback before dimensions are known
      if (activeTab === "portrait")
        return /port|vert|portrait/i.test(it.filename);
      if (activeTab === "landscape")
        return /land|wide|scape/i.test(it.filename);
      return true;
    });
    const bySearch = byTab.filter((it) =>
      it.filename.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const sorted = [...bySearch].sort((a, b) => {
      if (sort === "az") return a.filename.localeCompare(b.filename);
      if (sort === "za") return b.filename.localeCompare(a.filename);
      if (sort === "newest") {
        return (
          (b.created ? b.created.getTime() : 0) -
          (a.created ? a.created.getTime() : 0)
        );
      }
      if (sort === "oldest") {
        return (
          (a.created ? a.created.getTime() : 0) -
          (b.created ? b.created.getTime() : 0)
        );
      }
      return 0;
    });

    return sorted;
  }, [
    userPictures,
    searchQuery,
    activeTab,
    sort,
    folderMap,
    selectedFolder,
    favorites,
    imgDims,
    dateRange,
    createdMap,
    justUploaded,
  ]);
  const fmtRangeLabel = () => {
    const { start, end } = dateRange || {};
    if (!start && !end) return "All time";
    const fmt = (d) =>
      d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    if (start && end) return `${fmt(start)} – ${fmt(end)}`;
    if (start) return `${fmt(start)} – now`;
    return `… – ${fmt(end)}`;
  };
  const visibleUrls = filtered.map((it) => it.url);
  const allVisibleSelected =
    visibleUrls.length > 0 && visibleUrls.every((u) => selectedSet.has(u));
  const anySelected = selectedSet.size > 0;
  // Bulk selection: are all selected items favorites?
  const allSelectedFav =
    selectedSet.size > 0 &&
    Array.from(selectedSet).every((u) => favorites.has(u));

  const toggleSelect = (url) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        // unselect only visible ones
        visibleUrls.forEach((u) => next.delete(u));
      } else {
        // select all visible ones
        visibleUrls.forEach((u) => next.add(u));
      }
      return next;
    });
  };

  // Stats (fake trends for now)
  const total = userPictures.length;
  const fulfilled = Math.round(total * 0.6); // placeholder metric
  const returns = Math.max(0, Math.round(total * 0.02));
  const overTime = Math.max(0, total - Math.round(total * 0.18));

  const addFolderByName = async (name) => {
    const clean = (name || "").trim();
    if (!uid || !clean) return;
    if (["All", "Unsorted"].includes(clean)) return;
    // Avoid duplicates (case-insensitive)
    const exists = folders.some((f) => f.toLowerCase() === clean.toLowerCase());
    if (exists) return;
    await setDoc(doc(collection(db, "imageFolders", uid, "folders")), {
      name: clean,
    });
    setFolders((prev) => Array.from(new Set([...(prev || []), clean])));
  };

  const addFolder = async () => {
    const name = (newFolderName || "").trim();
    await addFolderByName(name);
    setNewFolderName("");
  };

  const idForUrl = (url) => {
    try {
      return btoa(encodeURIComponent(url));
    } catch {
      return url.replace(/[^a-z0-9]/gi, "_");
    }
  };
  const safeName = (name, extHint) => {
    const base = String(name || "image")
      .trim()
      .replace(/[^a-z0-9._-]+/gi, "-");
    if (/[.]/.test(base)) return base; // already has extension

    return base;
  };

  const moveImageTo = async (url, folder) => {
    if (!uid) return;
    const id = idForUrl(url);
    await setDoc(
      doc(db, "imageFolders", uid, "items", id),
      { url, folder },
      { merge: true }
    );
    setFolderMap((m) => ({ ...m, [url]: folder }));
  };

  // Bulk favorites
  const bulkSetFavorite = async (urls, willFav) => {
    // optimistic UI
    setFavorites((prev) => {
      const next = new Set(prev);
      urls.forEach((u) => {
        if (willFav) next.add(u);
        else next.delete(u);
      });
      return next;
    });
    setMenuOpenFor(null);
    setBulkOpen(false);
    // persist
    try {
      if (!uid) return;
      await Promise.all(
        urls.map((url) => {
          const id = idForUrl(url);
          return setDoc(
            doc(db, "imageFolders", uid, "items", id),
            { url, favorite: willFav },
            { merge: true }
          );
        })
      );
    } catch (e) {
      console.warn("[ImageHistory] bulk favorite failed, reverting", e);
      // revert
      setFavorites((prev) => {
        const next = new Set(prev);
        urls.forEach((u) => {
          if (willFav) next.delete(u);
          else next.add(u);
        });
        return next;
      });
    }
  };

  // Bulk move
  const bulkMoveTo = async (urls, folder) => {
    if (!uid || !folder) return;
    // optimistic UI
    setFolderMap((m) => {
      const copy = { ...m };
      urls.forEach((u) => {
        copy[u] = folder;
      });
      return copy;
    });
    setBulkOpen(false);
    try {
      await Promise.all(
        urls.map((url) => {
          const id = idForUrl(url);
          return setDoc(
            doc(db, "imageFolders", uid, "items", id),
            { url, folder },
            { merge: true }
          );
        })
      );
    } catch (e) {
      console.warn("[ImageHistory] bulk move failed", e);
      // Note: For simplicity we are not reverting folderMap here; user can re-run.
    }
  };

  const submitInlineNew = async (place) => {
    await addFolder();
    if (place === "top") setShowAddTop(false);
    if (place === "side") setShowAddSide(false);
  };

  const handleDownload = (url) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setMenuOpenFor(null);
  };
  const handleDelete = (url) => {
    setMenuOpenFor(null);
    console.warn("[ImageHistory] TODO: wire deletion for", url);
    // You can hook Firebase Storage deletion here later.
  };
  const handleRename = (url) => {
    const newName = prompt("Rename to:");
    setMenuOpenFor(null);
    if (!newName) return;
    console.warn("[ImageHistory] TODO: persist rename for", url, "->", newName);
    // Persist custom display name in Firestore if desired.
  };
  // AFTER - optimistic + persisted
  const toggleFavorite = async (url) => {
    const willFav = !favorites.has(url);

    // optimistic UI
    setFavorites((prev) => {
      const next = new Set(prev);
      if (willFav) next.add(url);
      else next.delete(url);
      return next;
    });
    setMenuOpenFor(null);

    // persist
    try {
      if (!uid) return;
      const id = idForUrl(url);
      await setDoc(
        doc(db, "imageFolders", uid, "items", id),
        { url, favorite: willFav },
        { merge: true }
      );
    } catch (e) {
      console.warn("[ImageHistory] favorite toggle failed, reverting", e);
      // revert on failure
      setFavorites((prev) => {
        const next = new Set(prev);
        if (willFav) next.delete(url);
        else next.add(url);
        return next;
      });
    }
  };
  const onThumbLoad = (url, ev) => {
    const el = ev.currentTarget;
    const w = el.naturalWidth || el.width;
    const h = el.naturalHeight || el.height;
    if (!w || !h) return;
    setImgDims((prev) => {
      const cur = prev[url];
      if (cur && cur.w === w && cur.h === h) return prev; // no change
      return { ...prev, [url]: { w, h } };
    });
  };

  return (
    <div className="ihx-wrap">
      {/* Header Row */}
      <div className="ihx-header">
        <div className="cp-orders__toolbarTitle">
          <div className="cp-orders__sectionTitle">Images</div>
          <div className="cp-orders__sectionSub">
            Manage, view, and organize your uploaded pictures.
          </div>
        </div>
        <div className="ihx-header__controls">
          <div className="cu-cell cu-cell--action" ref={timelineWrapRef}>
            <button
              className="ihx-btn ihx-btn--ghost ihx-btn--icon"
              onClick={() => setTimelineOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={timelineOpen}
              type="button"
            >
              <HiOutlineClock className="ihx-icon" />
              <span>{fmtRangeLabel()}</span>
              <HiChevronDown className="ihx-caret" />
            </button>
            {timelineOpen && (
              <ul
                className="ihx-actionMenu ihx-actionMenu--wide"
                role="menu"
                style={{ minWidth: 280 }}
              >
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setDateRange({ start: null, end: null });
                      setTimelineOpen(false);
                    }}
                  >
                    All time
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      const end = new Date();
                      const start = new Date();
                      start.setDate(end.getDate() - 6);
                      setDateRange({
                        start: new Date(
                          start.getFullYear(),
                          start.getMonth(),
                          start.getDate()
                        ),
                        end: new Date(
                          end.getFullYear(),
                          end.getMonth(),
                          end.getDate()
                        ),
                      });
                      setTimelineOpen(false);
                    }}
                  >
                    Last 7 days
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      const end = new Date();
                      const start = new Date();
                      start.setDate(end.getDate() - 29);
                      setDateRange({
                        start: new Date(
                          start.getFullYear(),
                          start.getMonth(),
                          start.getDate()
                        ),
                        end: new Date(
                          end.getFullYear(),
                          end.getMonth(),
                          end.getDate()
                        ),
                      });
                      setTimelineOpen(false);
                    }}
                  >
                    Last 30 days
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      const now = new Date();
                      const start = new Date(now.getFullYear(), 0, 1);
                      const end = new Date(
                        now.getFullYear(),
                        now.getMonth(),
                        now.getDate()
                      );
                      setDateRange({ start, end });
                      setTimelineOpen(false);
                    }}
                  >
                    This year
                  </button>
                </li>
                <li className="ihx-dividerLine" aria-hidden="true" />
                <li style={{ padding: 8 }}>
                  <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                    Custom range
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      className="ihx-input"
                    />
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      className="ihx-input"
                    />
                    <button
                      className="ihx-btn ihx-btn--primary"
                      onClick={() => {
                        const s = customStart
                          ? new Date(customStart + "T00:00:00")
                          : null;
                        const e = customEnd
                          ? new Date(customEnd + "T00:00:00")
                          : null;
                        setDateRange({ start: s, end: e });
                        setTimelineOpen(false);
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </li>
              </ul>
            )}
          </div>
          <div className="cu-cell cu-cell--action" ref={moreWrapRef}>
            <button
              className="ihx-btn ihx-btn--ghost ihx-btn--icon"
              onClick={() => setMoreOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              type="button"
            >
              <span className="ihx-icon">More actions</span>
              <HiChevronDown className="ihx-caret" />
            </button>
            {moreOpen && (
              <ul className="ihx-actionMenu" role="menu">
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMoreOpen(false);
                      openManage();
                    }}
                  >
                    <span className="ihx-menuLabel">Manage Folders</span>
                  </button>
                </li>
              </ul>
            )}
          </div>
          <button className="ihx-btn ihx-btn--primary" onClick={openFilePicker}>
            Upload images
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={onFileChosen}
          />
        </div>
      </div>

      {/* Stat Cards */}
      <ul className="ihx-folderList">
        {folders.filter(Boolean).map((name) => (
          <li key={name}>
            <button
              className={`ihx-folder ${selectedFolder === name ? "is-active" : ""}`}
              onClick={() => setSelectedFolder(name)}
            >
              <HiFolder className="ihx-folder__icon" />
              <span className="ihx-folder__name">{name}</span>
            </button>
          </li>
        ))}
        {showAddTop ? (
          <li>
            <div className="ihx-inlineNew">
              <input
                autoFocus
                className="ihx-inlineNew__input"
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitInlineNew("top");
                  if (e.key === "Escape") setShowAddTop(false);
                }}
              />
              <button
                className="ihx-inlineNew__btn ihx-inlineNew__btn--primary"
                onClick={() => submitInlineNew("top")}
              >
                Create
              </button>
              <button
                className="ihx-inlineNew__btn"
                onClick={() => setShowAddTop(false)}
              >
                Cancel
              </button>
            </div>
          </li>
        ) : (
          <li>
            <button
              className="ihx-folder ihx-folder--add"
              onClick={() => {
                setNewFolderName("");
                setShowAddTop(true);
              }}
            >
              ＋
            </button>
          </li>
        )}
      </ul>

      {/* Tabs + Toolbar */}
      <div className="ihx-bar">
        <div className="ihx-tabs">
          {[
            { key: "all", label: "All" },
            { key: "favorites", label: "Favorites" },
            { key: "portrait", label: "Portrait" },
            { key: "landscape", label: "Landscape" },
          ].map((t) => (
            <button
              key={t.key}
              className={`ihx-tab ${activeTab === t.key ? "is-active" : ""}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="ihx-toolbar">
          {/* Bulk Actions dropdown for multiple selection */}
          {selectedSet.size > 1 && (
            <div className="cu-cell cu-cell--action" ref={bulkWrapRef}>
              <button
                type="button"
                className="ihx-btn ihx-btn--ghost"
                onClick={() => setBulkOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={bulkOpen}
              >
                Bulk Actions ({selectedSet.size}){" "}
                <HiChevronDown className="ihx-caret" />
              </button>
              {bulkOpen && (
                <ul className="ihx-actionMenu" role="menu">
                  <li>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() =>
                        bulkSetFavorite(
                          Array.from(selectedSet),
                          !allSelectedFav
                        )
                      }
                    >
                      <span className="ihx-menuIcon">
                        {allSelectedFav ? <HiStar /> : <HiOutlineStar />}
                      </span>
                      <span className="ihx-menuLabel">
                        {allSelectedFav
                          ? "Remove from Favorites"
                          : "Add to Favorites"}
                      </span>
                    </button>
                  </li>
                  <li className="ihx-dividerLine" aria-hidden="true" />
                  <li style={{ padding: 8 }}>
                    <div
                      style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}
                    >
                      Move to folder
                    </div>
                    <FolderSelect
                      folders={folders}
                      value={
                        selectedFolder === "All" ? "Unsorted" : selectedFolder
                      }
                      onChange={(val) =>
                        bulkMoveTo(Array.from(selectedSet), val)
                      }
                      onCreateFolder={addFolderByName}
                    />
                  </li>
                </ul>
              )}
            </div>
          )}
          <div className="ihx-search">
            <input
              className="ihx-search__input"
              placeholder="Search by filename"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            className="ihx-btn ihx-btn--ghost"
            title={allVisibleSelected ? "Unselect all" : "Select all"}
            onClick={toggleSelectAllVisible}
          >
            {allVisibleSelected ? "Unselect all" : "Select all"}
          </button>
          <div className="ihx-divider" />
          <div className="cu-cell cu-cell--action" ref={sortWrapRef}>
            <button
              type="button"
              className="ihx-btn ihx-btn--ghost ihx-btn--icon"
              onClick={() => setSortOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={sortOpen}
            >
              <span className="ihx-icon">
                Sort:{" "}
                {sort === "newest"
                  ? "Newest"
                  : sort === "oldest"
                    ? "Oldest"
                    : sort === "az"
                      ? "A–Z"
                      : "Z–A"}
              </span>
              <HiChevronDown className="ihx-caret" />
            </button>

            {sortOpen && (
              <ul className="ihx-actionMenu" role="menu">
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSort("newest");
                      setSortOpen(false);
                    }}
                  >
                    <span className="ihx-menuLabel">Newest</span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSort("oldest");
                      setSortOpen(false);
                    }}
                  >
                    <span className="ihx-menuLabel">Oldest</span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSort("az");
                      setSortOpen(false);
                    }}
                  >
                    <span className="ihx-menuLabel">A–Z</span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSort("za");
                      setSortOpen(false);
                    }}
                  >
                    <span className="ihx-menuLabel">Z–A</span>
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Body with folder sidebar */}

      <section className="ihx-content">
        <div className={`ihx-grid ${view === "list" ? "is-list" : ""}`}>
          {filtered.map(({ url, filename, folder }, idx) => (
            <div className="ihx-card" key={idx}>
              <div
                className="ihx-card__thumb"
                onClick={() => setSelectedImage && setSelectedImage(url)}
              >
                <label
                  className="ihx-checkbox ihx-checkbox--overlay"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={isSelected(url)}
                    onChange={() => toggleSelect(url)}
                  />
                  <span />
                </label>
                <img
                  className="ihx-thumb"
                  src={url}
                  alt={filename}
                  loading="lazy"
                  onLoad={(e) => onThumbLoad(url, e)}
                />
              </div>

              <div className="ihx-card__meta">
                <div className="ihx-filename" title={filename}>
                  {filename}
                </div>
                <FolderSelect
                  folders={folders}
                  value={folder}
                  onChange={(val) => moveImageTo(url, val)}
                  onCreateFolder={addFolderByName}
                />
                <div
                  className="cu-cell cu-cell--action"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="cu-icon"
                    aria-label="more"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenFor(menuOpenFor === url ? null : url);
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <circle cx="5" cy="12" r="2" fill="currentColor" />
                      <circle cx="12" cy="12" r="2" fill="currentColor" />
                      <circle cx="19" cy="12" r="2" fill="currentColor" />
                    </svg>
                  </button>
                  {menuOpenFor === url && (
                    <ul className="ihx-actionMenu" role="menu">
                      <li>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => handleDownload(url)}
                        >
                          <span className="ihx-menuIcon">
                            <HiArrowDownTray />
                          </span>
                          <span className="ihx-menuLabel">Download</span>
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => handleDelete(url)}
                        >
                          <span className="ihx-menuIcon">
                            <HiTrash />
                          </span>
                          <span className="ihx-menuLabel">Delete</span>
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => handleRename(url)}
                        >
                          <span className="ihx-menuIcon">
                            <HiPencilSquare />
                          </span>
                          <span className="ihx-menuLabel">Rename</span>
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => toggleFavorite(url)}
                        >
                          {favorites.has(url) ? (
                            <>
                              <span className="ihx-menuIcon">
                                <HiStar />
                              </span>
                              <span className="ihx-menuLabel">
                                Remove from Favorites
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="ihx-menuIcon">
                                <HiOutlineStar />
                              </span>
                              <span className="ihx-menuLabel">
                                Add to Favorites
                              </span>
                            </>
                          )}
                        </button>
                      </li>
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Upload overlay modal */}
      {uploadOpen && (
        <div className="ih-modal" onClick={closeUploadOverlay}>
          <div className="ihx-upload" onClick={(e) => e.stopPropagation()}>
            <div className="ihx-upload__head">
              <h3>Prepare Upload</h3>
              <button
                className="ihx-upload__close"
                onClick={closeUploadOverlay}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="ihx-upload__body">
              {uploadItems.map((it, i) => (
                <div className="ihx-upload__row" key={i}>
                  <img
                    className="ihx-upload__thumb"
                    src={it.preview}
                    alt="preview"
                  />
                  <input
                    className="ihx-upload__name"
                    value={it.name}
                    onChange={(e) => {
                      const v = e.target.value;
                      setUploadItems((arr) =>
                        arr.map((x, idx) => (idx === i ? { ...x, name: v } : x))
                      );
                    }}
                    placeholder="File name"
                  />
                </div>
              ))}
            </div>
            <div className="ihx-upload__foot">
              <div className="ihx-upload__bulkFolder">
                <FolderSelect
                  folders={folders}
                  value={uploadFolder}
                  onChange={(val) => setUploadFolder(val)}
                  onCreateFolder={addFolderByName}
                />
              </div>
              <div className="ihx-upload__footBtns">
                <button className="ihx-btn" onClick={closeUploadOverlay}>
                  Cancel
                </button>
                <button
                  className="ihx-btn ihx-btn--primary"
                  onClick={confirmUpload}
                >
                  Upload {uploadItems.length}{" "}
                  {uploadItems.length === 1 ? "item" : "items"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Folders modal */}
      {manageOpen && (
        <div className="ih-modal" onClick={() => setManageOpen(false)}>
          <div className="ihx-manage" onClick={(e) => e.stopPropagation()}>
            <div className="ihx-manage__head">
              <h3>Manage Folders</h3>
              <button
                className="ihx-manage__close"
                onClick={() => setManageOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="ihx-manage__body">
              {manageRows.length === 0 && (
                <div className="ihx-manage__empty">No custom folders yet.</div>
              )}
              {manageRows.map((row, i) => (
                <div className="ihx-manage__row" key={row.name}>
                  <input
                    className="ihx-manage__name"
                    value={row.edit}
                    onChange={(e) => {
                      const v = e.target.value;
                      setManageRows((arr) =>
                        arr.map((r, idx) => (idx === i ? { ...r, edit: v } : r))
                      );
                    }}
                  />
                  <div className="ihx-manage__actions">
                    <button
                      className="ihx-btn"
                      onClick={() => renameFolder(row.name, row.edit)}
                    >
                      Save
                    </button>
                    <button
                      className="ihx-btn"
                      onClick={() => deleteFolderByName(row.name)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="ihx-manage__foot">
              <button className="ihx-btn" onClick={() => setManageOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <div className="ih-modal" onClick={() => setSelectedImage(null)}>
          <img
            src={selectedImage}
            alt="Enlarged view"
            className="ih-modalImg"
          />
        </div>
      )}
    </div>
  );
}
